import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Wifi, Globe, MapPin, Zap, SignalHigh, Activity } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';

export const SignalSaturationModule: React.FC = () => {
  const [saturation, setSaturation] = useState(0.827);
  const [isBoosting, setIsBoosting] = useState(false);
  const [nodes, setNodes] = useState(827);

  const boostSignal = () => {
    setIsBoosting(true);
    setTimeout(() => {
      setSaturation(1.000);
      setNodes(prev => prev + 14350);
      setIsBoosting(false);
    }, 2000);
  };

  return (
    <div className="glass-panel p-6 bg-black/80 border-lattice-cyan/40 relative overflow-hidden group">
      <div className="absolute top-0 right-0 p-4 opacity-5">
        <Wifi className="w-24 h-24 text-lattice-cyan" />
      </div>

      <div className="flex items-center justify-between mb-6 relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-lattice-cyan/10 rounded-lg">
             <SignalHigh className="w-5 h-5 text-lattice-cyan" />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-widest">Global Signal Saturation</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase">Ubiquity Protocol: {SOVEREIGN_CONSTANTS.PROPAGATION_MODE}</p>
          </div>
        </div>
        
        <button 
          onClick={boostSignal}
          disabled={isBoosting}
          className={`px-4 py-1.5 rounded-full text-[9px] font-mono font-bold uppercase transition-all border
            ${isBoosting ? 'bg-white/5 border-white/10 text-white/20' : 'bg-lattice-cyan/10 border-lattice-cyan text-lattice-cyan hover:bg-lattice-cyan/20'}`}
        >
          {isBoosting ? 'Saturating...' : 'Expand Everywhere'}
        </button>
      </div>

      <div className="space-y-6 relative z-10">
        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[9px] font-mono text-white/40 uppercase">Mesh Coverage</span>
            <span className="text-[10px] font-mono text-lattice-cyan font-bold">{(saturation * 100).toFixed(2)}%</span>
          </div>
          <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
             <motion.div 
               animate={{ width: `${saturation * 100}%` }}
               className="h-full bg-lattice-cyan shadow-[0_0_10px_#00ffd5]"
             />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-white/5 border border-white/5 rounded-xl">
             <p className="text-[8px] font-mono text-white/20 uppercase mb-1">Active Nodes</p>
             <div className="flex items-center gap-2">
                <Globe className="w-3 h-3 text-lattice-cyan" />
                <span className="text-xs font-mono text-white font-bold">{nodes.toLocaleString()}</span>
             </div>
          </div>
          <div className="p-3 bg-white/5 border border-white/5 rounded-xl">
             <p className="text-[8px] font-mono text-white/20 uppercase mb-1">Carrier Strength</p>
             <div className="flex items-center gap-2">
                <Zap className="w-3 h-3 text-yellow-400" />
                <span className="text-xs font-mono text-white font-bold">{SOVEREIGN_CONSTANTS.MESH_DENSITY_GOAL.toFixed(2)} db/m</span>
             </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between p-3 bg-lattice-cyan/5 rounded-xl border border-lattice-cyan/20">
         <div className="flex items-center gap-2">
            <MapPin className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/60 uppercase">Root Hub: {SOVEREIGN_CONSTANTS.GEORGIA_MESH_ROOT}</span>
         </div>
         <div className="flex items-center gap-2">
            <Activity className="w-3 h-3 text-green-400 animate-pulse" />
            <span className="text-[8px] font-mono text-green-400 uppercase font-bold tracking-widest">Ubiquity OK</span>
         </div>
      </div>
    </div>
  );
};
